import { BreakpointObserver } from '@angular/cdk/layout';
import { Inject, Injectable, signal, WritableSignal } from '@angular/core';
import { GalaxyColumnDef } from '@vendasta/galaxy/table';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { BREAKPOINT_WIDTH } from '../../../constants';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';

interface TableState {
  localStorageKey: string[];
  filterBarOpenStorageKey: string;
}

@Injectable()
export class CrmTableStateService {
  MOBILE_BREAKPOINT = `(max-width: ${BREAKPOINT_WIDTH.MOBILE})`;
  isMobile$: Observable<boolean> = this.breakpointObserver.observe([this.MOBILE_BREAKPOINT]).pipe(
    map((result) => result.matches),
    distinctUntilChanged(),
  );
  tableState: WritableSignal<TableState> = signal({
    localStorageKey: [],
    filterBarOpenStorageKey: '',
  });

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly breakpointObserver: BreakpointObserver,
  ) {}

  initializeLocalStorageColumns(columns: GalaxyColumnDef[]): Observable<GalaxyColumnDef[]> {
    return of(false).pipe(
      map((isMobile) => {
        if (isMobile) {
          return columns;
        }
        let lcColumns: GalaxyColumnDef[] = [];
        for (const i in this.tableState().localStorageKey) {
          const ls = localStorage.getItem(this.tableState().localStorageKey[i]);
          if (!ls) {
            continue;
          }
          lcColumns = JSON.parse(ls);
          break;
        }
        columns.forEach((column) => {
          const localStorageColumnIndex = lcColumns?.findIndex((c) => c.id === column.id);
          if (localStorageColumnIndex < 0) {
            lcColumns.push(column);
          } else {
            const localStorageColumn = lcColumns[localStorageColumnIndex];
            lcColumns[localStorageColumnIndex] = {
              ...column,
              _position: localStorageColumn._position,
              hidden: localStorageColumn.hidden,
            };
          }
        });
        const lcColumnsCleaned = lcColumns.filter((c) => columns.find((column) => column.id === c.id));
        const sortedColumns = this.sortColumns(lcColumnsCleaned);

        localStorage.setItem(this.tableState().localStorageKey[0], JSON.stringify(sortedColumns));
        return sortedColumns;
      }),
    );
  }

  private sortColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const topColumns = this.sortColumnsByPosition(columns.filter((column) => column.pinned && !column.stickyEnd));
    const bottomColumns = this.sortColumnsByPosition(columns.filter((column) => column.pinned && column.stickyEnd));
    const nonPinnedColumns = this.sortColumnsByPosition(columns.filter((column) => !column.pinned));

    const sortedColumns = [...topColumns, ...nonPinnedColumns, ...bottomColumns];
    this.updateColumnsPosition(sortedColumns);
    return sortedColumns;
  }

  private updateColumnsPosition(columns: GalaxyColumnDef[]): void {
    columns.forEach((column, index) => (column._position = index));
  }

  private sortColumnsByPosition(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    return columns.sort((a, b) => (a._position ?? Number.MAX_SAFE_INTEGER) - (b._position ?? Number.MAX_SAFE_INTEGER));
  }

  public setLocalStorageColumns(columns: GalaxyColumnDef[]): void {
    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      if (!columns?.length || isMobile) {
        return;
      }
      localStorage.setItem(this.tableState().localStorageKey[0], JSON.stringify(columns));
    });
  }

  setLocalStorageKey(objectType: ObjectType, objectSubtype = ''): void {
    const localStorageKey: string[] = [];
    let filterBarOpenStorageKey = '';
    const appName = this.config.appID;
    let type = objectType;
    if (objectSubtype) {
      type += `-${objectSubtype}`;
    }
    if (appName) {
      localStorageKey.push(`crm-${appName}-${type}-table-state-v2`);
      localStorage.removeItem(`crm-${appName}-${type}-table-state`);
      filterBarOpenStorageKey = `crm-${appName}-${type}-filterbar-state`;
    } else {
      filterBarOpenStorageKey = `crm-${type}-filterbar-state`;
    }
    localStorageKey.push(`crm-${type}-table-state-v2`);
    localStorage.removeItem(`crm-${type}-table-state`);
    this.tableState.set({
      localStorageKey: localStorageKey,
      filterBarOpenStorageKey: filterBarOpenStorageKey,
    });
  }
}
